/**
 * Business Inbox — FASE 11.
 * Toda captura llega aquí: OCR, vision, proposals, confidence, timeline.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RenataFeatureGate } from "@/components/renata/RenataFeatureGate";
import { useRenataFlag } from "@/hooks/use-renata-flags";
import { useEffect, useRef, useState } from "react";
import { Inbox, Upload, RefreshCw, Eye, FileText, AlertTriangle, ClipboardCheck, Loader2, ScanText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { presentCaptureProposals } from "@/lib/renata/capture/present";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { INBOX_STATUS_ES, DOC_CLASS_ES, CONFIDENCE_ES, labelStatus } from "@/lib/renata/experience/status-labels";

export const Route = createFileRoute("/app/renata/inbox")({
  component: BusinessInboxPage,
  head: () => ({ meta: [{ title: "Bandeja de entrada — Renata" }] }),
});

function BusinessInboxPage() {
  const tenantId = useWorkspaceTenantId();
  const canCapture = useRenataFlag("ocr_capture");
  const canReview  = useRenataFlag("guided_review");
  const navigate = useNavigate();
  const [docs, setDocs] = useState<Array<Record<string, unknown>>>([]);
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>("");
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function load() {
    if (!tenantId) return;
    const qs = filter ? `?status=${encodeURIComponent(filter)}` : "";
    const res = await fetch(`/api/renata/capture${qs}`, {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    });
    if (res.ok) {
      const d = await res.json();
      setDocs(d.documents ?? []);
    }
  }

  useEffect(() => { void load(); }, [tenantId, filter]);

  async function upload(files: FileList | null, source = "upload") {
    if (!tenantId || !files?.length) return;
    const firstName = files[0]?.name ?? "";
    setLoading(true);
    setUploadingFile(files.length > 1 ? `${files.length} archivos` : firstName);
    try {
      const fd = new FormData();
      fd.append("source", source);
      fd.append("action", files.length > 1 ? "batch" : "process");
      for (const f of Array.from(files)) fd.append("file", f);
      const res = await fetch("/api/renata/capture", {
        method: "POST",
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
        body: fd,
      });
      const d = await res.json();
      if (d.result) setResult(d.result);
      await load();
    } finally {
      setLoading(false);
      setUploadingFile(null);
    }
  }

  async function openDoc(id: string) {
    if (!tenantId) return;
    const res = await fetch(`/api/renata/capture?view=document&id=${id}`, {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    });
    if (res.ok) {
      const d = await res.json();
      setSelected(d.document);
    }
  }

  async function correctAndReprocess() {
    if (!tenantId || !selected) return;
    const corrected = String(selected.corrected_ocr_text ?? (selected.ocr_json as { text?: string })?.text ?? "");
    const res = await fetch("/api/renata/capture", {
      method: "POST",
      credentials: "include",
      headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "review_correct",
        id: selected.id,
        correctedOcrText: corrected,
      }),
    });
    const d = await res.json();
    setResult(d.result ?? null);
    await load();
  }

  async function decideProposal(decision: "approved" | "rejected") {
    if (!tenantId || !selected?.id) return;
    setBusy(true);
    try {
      const res = await fetch("/api/renata/capture", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "proposal_decision", id: selected.id, decision }),
      });
      const data = (await res.json()) as { ok?: boolean; note?: string; error?: string };
      if (!res.ok) throw new Error(data.error ?? "No se pudo registrar la decisión");
      toast.success(
        decision === "approved"
          ? "Propuesta aceptada. La ejecución real sigue en Aprobaciones / Acciones de Renata."
          : "Propuesta rechazada.",
      );
      if (data.note) toast.message(data.note);
      setSelected({ ...selected, status: decision === "approved" ? "approved" : "rejected" });
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <RenataFeatureGate featureId="inbox">
    <>
    {uploadingFile && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 rounded-2xl bg-white dark:bg-zinc-900 px-10 py-8 shadow-2xl">
          <div className="relative flex items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <ScanText className="absolute h-5 w-5 text-primary/60" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-foreground">Analizando documento…</p>
            <p className="mt-1 max-w-[220px] truncate text-xs text-muted-foreground">{uploadingFile}</p>
            <p className="mt-1 text-xs text-muted-foreground">Esto puede tardar unos segundos</p>
          </div>
        </div>
      </div>
    )}
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RenataPageChrome
          screenId="inbox"
          title="Bandeja de entrada"
          subtitle="Subí fotos o PDFs. Renata lee y propone cambios; vos confirmás. Nada se aplica solo."
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
          </Button>
          {canCapture && (
            <Button
              size="sm"
              className="bg-violet-600 hover:bg-violet-700"
              disabled={loading}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5 mr-1" />
              {loading ? "Procesando…" : "Subir / capturar"}
            </Button>
          )}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.tif,.tiff,.docx,.txt,.csv,.xlsx,image/*,application/pdf"
            onChange={(e) => void upload(e.target.files)}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {([
          ["", "Todos"],
          ["needs_review", "Para revisar"],
          ["proposal_pending", "Propuesta pendiente"],
          ["processed", "Procesados"],
          ["error", "Con error"],
        ] as const).map(([s, label]) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setFilter(s)}
            className={`rounded-full border px-3 py-1 ${filter === s ? "bg-violet-100 border-violet-300" : ""}`}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Estados: <strong>Para revisar</strong> = Renata necesita tu ojo · <strong>Propuesta pendiente</strong> = hay un cambio listo para aprobar · <strong>Procesado</strong> = listo.
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          {docs.length === 0 && (
            <p className="text-sm text-muted-foreground">Bandeja vacía. Subí un documento o imagen.</p>
          )}
          {docs.map((d) => (
            <button
              key={String(d.id)}
              type="button"
              onClick={() => void openDoc(String(d.id))}
              className="w-full text-left rounded-xl border px-3 py-2 hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate flex-1">{String(d.filename)}</span>
                <Badge variant="secondary">{DOC_CLASS_ES[String(d.doc_class)] ?? String(d.doc_class)}</Badge>
              </div>
              <div className="mt-1 flex gap-2 text-[11px] text-muted-foreground">
                <span>{labelStatus(INBOX_STATUS_ES, String(d.status))}</span>
                <span>confianza: {CONFIDENCE_ES[String(d.overall_confidence)] ?? String(d.overall_confidence)}</span>
                {d.created_at && (
                  <span className="ml-auto shrink-0">
                    {new Date(String(d.created_at)).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
                  </span>
                )}
                {d.needs_review && String(d.status) !== "processed" ? (
                  <span className="text-amber-600 flex items-center gap-0.5">
                    <AlertTriangle className="h-3 w-3" /> revisar
                  </span>
                ) : null}
              </div>
            </button>
          ))}
        </div>

        <div className="rounded-xl border p-4 space-y-3 min-h-[240px]">
          {!selected && !result && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Eye className="h-4 w-4" /> Seleccioná un documento
            </p>
          )}
          {selected && (
            <>
              <h2 className="font-semibold text-sm">{String(selected.filename)}</h2>
              <div className="flex flex-wrap gap-2">
                <Badge>{DOC_CLASS_ES[String(selected.doc_class)] ?? String(selected.doc_class)}</Badge>
                <Badge variant="outline">{CONFIDENCE_ES[String(selected.overall_confidence)] ?? String(selected.overall_confidence)}</Badge>
                <Badge variant="secondary">{labelStatus(INBOX_STATUS_ES, String(selected.status))}</Badge>
              </div>
              <div>
                <p className="text-xs font-medium mb-1">Texto detectado (editable si requiere revisión)</p>
                <textarea
                  className="w-full min-h-[100px] rounded-lg border bg-background p-2 text-xs"
                  value={String(
                    selected.corrected_ocr_text
                      ?? (selected.ocr_json as { text?: string } | undefined)?.text
                      ?? "",
                  )}
                  onChange={(e) =>
                    setSelected({ ...selected, corrected_ocr_text: e.target.value })
                  }
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {canReview && String(selected.status) !== "processed" &&
                   (String(selected.status) === "proposal_pending" ||
                    String(selected.status) === "needs_review" ||
                    String(selected.doc_class) === "remito" ||
                    String(selected.doc_class) === "factura" ||
                    String(selected.doc_class) === "orden_compra") && (
                    <Button
                      size="sm"
                      variant="default"
                      className="gap-1.5"
                      onClick={() =>
                        void navigate({ to: `/app/renata/capture/${String(selected.id)}` })
                      }
                    >
                      <ClipboardCheck className="h-3.5 w-3.5" />
                      Revisión guiada
                    </Button>
                  )}
                  {Boolean(selected.needs_review) && (
                    <Button size="sm" variant="outline" onClick={() => void correctAndReprocess()}>
                      Corregir OCR y reprocesar
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-xs font-medium">Propuestas</p>
                {presentCaptureProposals(selected.proposals_json).length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No hay propuestas listas. Si el documento requiere revisión, corregí el texto y reprocesá.
                  </p>
                ) : (
                  presentCaptureProposals(selected.proposals_json).map((p, idx) => (
                    <div key={idx} className="rounded-lg border p-3 space-y-1.5 text-xs">
                      <p className="font-medium text-sm">{p.title}</p>
                      <p className="text-muted-foreground">{p.description}</p>
                      <p><span className="font-medium">Qué cambia:</span> {p.whatChanges}</p>
                      <p><span className="font-medium">Qué no cambia:</span> {p.whatDoesNot}</p>
                      {p.impact ? <p><span className="font-medium">Impacto:</span> {p.impact}</p> : null}
                      <p><span className="font-medium">Confianza:</span> {p.confidenceLabel}</p>
                      {p.sourceHint ? <p><span className="font-medium">Fuente:</span> {p.sourceHint}</p> : null}
                      <p className="text-muted-foreground">{p.nextStep}</p>
                    </div>
                  ))
                )}
                {String(selected.status) === "proposal_pending" || String(selected.status) === "needs_review" ? (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} onClick={() => void decideProposal("approved")}>
                      Aceptar propuesta
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => void decideProposal("rejected")}>
                      Rechazar
                    </Button>
                  </div>
                ) : null}
              </div>
            </>
          )}
          {result && (
            <div className="space-y-2 rounded-lg border p-3 text-xs">
              <p className="font-medium">Último análisis</p>
              <p>Tipo: {String(result.docClass ?? "—")}</p>
              <p>Confianza: {String(result.overallConfidence ?? "—")}</p>
              <p>
                {result.needsHumanReview
                  ? "Requiere revisión humana antes de proponer acciones."
                  : "Listo para revisar propuestas."}
              </p>
              {Array.isArray(result.proposals) && result.proposals.length > 0 ? (
                <div className="space-y-2">
                  {presentCaptureProposals(result.proposals).map((p, idx) => (
                    <div key={idx} className="rounded border bg-muted/20 p-2">
                      <p className="font-medium">{p.title}</p>
                      <p className="text-muted-foreground">{p.description}</p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
    </RenataFeatureGate>
  );
}
